/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ScrollText, Search, ChevronLeft, ChevronRight, Shield, UserCog, Key, Trash2,
  Pencil, Plus, Eye, Clock, Filter, LogIn, LogOut, UserPlus, FileCheck, X, Globe,
  Loader2, Download,
} from 'lucide-react';

const ACTION_META = {
  'user.login': { label: 'Giris Yapildi', icon: LogIn, color: 'bg-blue-100 text-blue-700' },
  'user.register': { label: 'Kayit Olundu', icon: UserPlus, color: 'bg-emerald-100 text-emerald-700' },
  'user.update': { label: 'Kullanici Guncellendi', icon: Pencil, color: 'bg-amber-100 text-amber-700' },
  'user.delete': { label: 'Kullanici Silindi', icon: Trash2, color: 'bg-red-100 text-red-700' },
  'user.role_changed': { label: 'Rol Degisti', icon: UserCog, color: 'bg-cyan-100 text-cyan-700' },
  'user.password_reset': { label: 'Sifre Sifirlandi', icon: Key, color: 'bg-orange-100 text-orange-700' },
  'role.create': { label: 'Rol Olusturuldu', icon: Plus, color: 'bg-green-100 text-green-700' },
  'role.update': { label: 'Rol Guncellendi', icon: Pencil, color: 'bg-blue-100 text-blue-700' },
  'role.delete': { label: 'Rol Silindi', icon: Trash2, color: 'bg-red-100 text-red-700' },
  'role.permissions_updated': { label: 'Yetkiler Guncellendi', icon: Key, color: 'bg-amber-100 text-amber-700' },
  'role.copy': { label: 'Rol Kopyalandi', icon: Shield, color: 'bg-violet-100 text-violet-700' },
  'kvkk.accept': { label: 'KVKK Onaylandi', icon: FileCheck, color: 'bg-teal-100 text-teal-700' },
  'smtp.update': { label: 'SMTP Ayarlari Guncellendi', icon: Globe, color: 'bg-indigo-100 text-indigo-700' },
};

const getActionMeta = (action) => ACTION_META[action] || { label: action, icon: Eye, color: 'bg-slate-100 text-slate-700' };

const ACTION_CATEGORIES = [
  { value: 'all', label: 'Tum Islemler' },
  { value: 'user.login', label: 'Giris' },
  { value: 'user.register', label: 'Kayit' },
  { value: 'user.update', label: 'Kullanici Guncelleme' },
  { value: 'user.delete', label: 'Kullanici Silme' },
  { value: 'user.role_changed', label: 'Rol Degisikligi' },
  { value: 'user.password_reset', label: 'Sifre Sifirlama' },
  { value: 'role', label: 'Rol Islemleri' },
  { value: 'kvkk', label: 'KVKK' },
];

const AuditLogsPage = () => {
  usePageTitle('Sistem Loglari');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const limit = 25;

  useEffect(() => { loadLogs(); }, [page, actionFilter, dateFrom, dateTo]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit, limit,
        action: actionFilter !== 'all' ? actionFilter : undefined,
        search: search || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const r = await api.get('/rbac/audit-logs', { params });
      setLogs(r.data.logs);
      setTotal(r.data.total);
    } catch { toast.error('Loglar yuklenemedi'); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadLogs(); };

  const clearFilters = () => {
    setSearch(''); setActionFilter('all'); setDateFrom(''); setDateTo(''); setPage(1);
    setTimeout(loadLogs, 0);
  };

  const handleExportCSV = () => {
    const headers = ['Tarih', 'Kullanici', 'Islem', 'Hedef', 'IP Adresi', 'Detay'];
    const rows = logs.map(l => [
      formatDateTime(l.created_at), l.user_name || '-', getActionMeta(l.action).label,
      l.target_type ? `${l.target_type}:${l.target_id || ''}` : '-',
      l.ip_address || '-', l.details ? JSON.stringify(l.details) : '-'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV indirildi');
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = search || actionFilter !== 'all' || dateFrom || dateTo;

  return (
    <div className="space-y-5" data-testid="audit-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-blue-600" /> Sistem Loglari
          </h1>
          <p className="text-sm text-slate-500">Toplam {total} kayit</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-audit-csv">
          <Download className="w-4 h-4 mr-1.5" /> CSV Indir
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Kullanici, islem veya IP ara..." value={search}
                  onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="audit-search" />
              </div>
              <Button type="submit" variant="secondary" size="sm"><Filter className="w-4 h-4 mr-1" /> Ara</Button>
              {hasFilters && (
                <Button type="button" variant="ghost" size="sm" onClick={clearFilters}><X className="w-4 h-4 mr-1" /> Temizle</Button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={actionFilter} onValueChange={v => { setActionFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-52" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-40" data-testid="audit-date-from" />
                <span className="text-slate-400 text-sm">-</span>
                <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                  className="w-40" data-testid="audit-date-to" />
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Logs */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
          ) : logs.length > 0 ? (
            <>
              <div className="divide-y divide-slate-100">
                {logs.map(log => {
                  const meta = getActionMeta(log.action);
                  const Icon = meta.icon;
                  return (
                    <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors" data-testid={`audit-log-${log.id}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${meta.color} border-0 text-xs`}>{meta.label}</Badge>
                          <span className="text-sm font-medium text-slate-800">{log.user_name || 'Sistem'}</span>
                        </div>
                        {log.details && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {Object.entries(log.details).map(([k, v]) => (
                              <span key={k} className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">
                                {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(log.created_at)}</span>
                          {log.ip_address && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{log.ip_address}</span>}
                          {log.target_type && <span>{log.target_type}:{log.target_id?.slice(0, 8)}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-500">Sayfa {page}/{totalPages} ({total} kayit)</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ScrollText className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Log bulunamadi</h3>
              <p className="text-slate-500 mt-1">{hasFilters ? 'Filtreleri degistirin' : 'Henuz islem kaydedilmemis'}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
